import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import AdminCityListingTitle from "../../../components/AdminLayout/AdminCityListingTitle"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import {
  CITY_SHADOW_EVENT_TABS,
  CITY_SHADOW_LIST_WITH,
  mapCityShadowEventRow,
} from "./adminCityShadowEventsData"

const PAGE_SIZE = 16

const categorySlug = category =>
  String(category || "")
    .toLowerCase()
    .replace(/\s+/g, "-")

/**
 * Admin Manage City — Shadow Working Events.
 * GET /api/v1/admin/sessions?city_id=&type=shadow
 */
const AdminCityShadowEvents = () => {
  const { cityId } = useParams()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.title = "Shadow Working Events | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, cityId])

  const buildListParams = useCallback(() => {
    const params = {
      "with[]": CITY_SHADOW_LIST_WITH,
      type: "shadow",
      page: currentPage,
      page_size: pageSize,
    }

    if (debouncedSearch) params.search = debouncedSearch
    if (cityId) params.city_id = cityId
    if (activeTab !== "all") params.status = activeTab

    return params
  }, [activeTab, cityId, currentPage, pageSize, debouncedSearch])

  const fetchSessions = useCallback(async () => {
    if (!cityId) {
      setRows([])
      setTotalItems(0)
      setIsLoading(false)
      setError("Missing city id.")
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminSessions, {
        params: buildListParams(),
      })

      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapCityShadowEventRow)
        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load sessions")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load sessions")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, cityId, currentPage])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  const handleView = row => {
    if (!row?.id) return
    navigate(`/admin/sessions/${row.id}`)
  }

  const skeletonCards = useMemo(
    () => Array.from({ length: 8 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  const itemLabel = activeTab === "active" ? "active events" : "events"

  return (
    <div className="admin-page admin-city-shadow-events">
      <div className="admin-city-shadow-events__toolbar">
        <AdminCityListingTitle
          label="Shadow Working Events"
          cityId={cityId}
        />
        <div className="admin-city-shadow-events__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by Title..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-city-shadow-events__search"
          />
        </div>
      </div>

      <div className="admin-city-shadow-events__tabs-row">
        <ListingTabs
          items={CITY_SHADOW_EVENT_TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div
        className="admin-city-shadow-events__grid"
        aria-busy={isLoading || undefined}
      >
        {isLoading
          ? skeletonCards.map(key => (
              <article
                key={key}
                className="admin-city-shadow-events__card admin-city-shadow-events__card--skeleton"
              >
                <div className="admin-city-shadow-events__skeleton admin-city-shadow-events__skeleton--line" />
                <div className="admin-city-shadow-events__skeleton admin-city-shadow-events__skeleton--title" />
                <div className="admin-city-shadow-events__skeleton admin-city-shadow-events__skeleton--desc" />
                <div className="admin-city-shadow-events__skeleton admin-city-shadow-events__skeleton--meta" />
              </article>
            ))
          : rows.map(row => (
              <article key={row.id} className="admin-city-shadow-events__card">
                <div className="admin-city-shadow-events__heading">
                  <TruncatedText className="admin-city-shadow-events__org">
                    {row.org}
                  </TruncatedText>
                  <span
                    className={`admin-city-shadow-events__badge admin-city-shadow-events__badge--${categorySlug(
                      row.category,
                    )}`}
                  >
                    {row.category}
                  </span>
                  {row.visibility ? (
                    <span
                      className={`admin-city-shadow-events__visibility admin-city-shadow-events__visibility--${row.visibilityKey}`}
                    >
                      {row.visibility}
                    </span>
                  ) : null}
                </div>

                <TruncatedText
                  as="h2"
                  className="admin-city-shadow-events__card-title"
                >
                  {row.title}
                </TruncatedText>
                {row.description ? (
                  <TruncatedText
                    as="p"
                    className="admin-city-shadow-events__desc"
                  >
                    {row.description}
                  </TruncatedText>
                ) : null}

                <div className="admin-city-shadow-events__meta">
                  <p className="admin-city-shadow-events__host">
                    <span>Host:</span> {row.host}
                  </p>
                  <p className="admin-city-shadow-events__time">
                    <span>{row.datetime}</span>
                    <span aria-hidden="true"> · </span>
                    <span>{row.duration}</span>
                  </p>
                </div>

                <div className="admin-city-shadow-events__footer">
                  <div className="admin-city-shadow-events__attendees">
                    {row.attendees.map((src, index) => (
                      <Avatar
                        key={`${row.id}-a-${index}`}
                        src={src}
                        name={`Attendee ${index + 1}`}
                        size="xs"
                        className="admin-city-shadow-events__attendee"
                      />
                    ))}
                    {row.attendeeExtra > 0 ? (
                      <span className="admin-city-shadow-events__attendee-extra">
                        +{row.attendeeExtra}
                      </span>
                    ) : null}
                  </div>
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    className="admin-city-shadow-events__view-btn"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => handleView(row)}
                  >
                    View Details
                  </CustomButton>
                </div>
              </article>
            ))}
      </div>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title={error ? "Couldn’t load events" : "No events found"}
          description={error || "Try adjusting your search or filters."}
          actionText={error ? "Try again" : undefined}
          onActionClick={error ? fetchSessions : undefined}
        />
      ) : null}

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel={itemLabel}
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-city-shadow-events__pagination"
      />
    </div>
  )
}

export default AdminCityShadowEvents
